import { ReactNode } from "react";
import { Link, useLocation } from "wouter";
import {
  Home,
  BarChart2,
  Package,
  LineChart,
  Menu,
  Database,
  ShoppingCart,
  FileText,
  Facebook,
  Warehouse,
  Clock,
  Tags,
  Download,
  DollarSign,
  TrendingUp,
  Megaphone,
  Wallet,
  MapPin
} from "lucide-react";
import { PmSidebarSection } from "@/components/pm/layout/pm-sidebar-section";
import { CsSidebarSection } from "@/components/customer-service/layout/cs-sidebar-section";
import byAcreLogo from "../../assets/byacre-logo.png";
import { 
  Sidebar, 
  SidebarContent, 
  SidebarFooter, 
  SidebarHeader, 
  SidebarInset, 
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem, 
  SidebarProvider, 
  SidebarTrigger,
  SidebarGroup
} from "@/components/ui/sidebar";
import { usePageAccess } from "@/hooks/use-page-access";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";

interface MainLayoutProps {
  children: ReactNode;
}

/**
 * Dealer sidebar entry. Visible if the user has admin role (covered by the
 * existing role-based pageAccess gate) OR has been individually granted
 * access via the dealer_page_access table (queried via /api/dealer/my-access).
 */
function DealerSidebarSection({ location, canAccessRole }: { location: string; canAccessRole: boolean }) {
  const { data } = useQuery<{ canAccess: boolean }>({
    queryKey: ["/api/dealer/my-access"],
    enabled: !canAccessRole,    // skip the extra request if role already grants
    staleTime: 60_000,
  });
  // Store locator admin: the API's own access rule (store-locator/access.ts), so a per-user grant added there shows here too.
  const { data: storeLocator } = useQuery<{ canAccess: boolean }>({
    queryKey: ["/api/store-locator/my-access"],
    staleTime: 60_000,
  });
  const dealerVisible = canAccessRole || !!data?.canAccess;
  const storeLocatorVisible = !!storeLocator?.canAccess;
  if (!dealerVisible && !storeLocatorVisible) return null;
  return (
    <>
      <SidebarMenuItem className="pt-4">
        <div className="mb-2 px-3 text-xs font-semibold text-byacre-royal-blue">B2B</div>
      </SidebarMenuItem>
      {dealerVisible && (
        <SidebarMenuItem>
          <SidebarMenuButton asChild data-active={location.startsWith("/dealer-applications")}>
            <Link href="/dealer-applications">
              <FileText className="h-4 w-4" />
              <span>Dealer Applications</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )}
      {storeLocatorVisible && (
        <SidebarMenuItem>
          <SidebarMenuButton asChild data-active={location.startsWith("/store-locator")}>
            <Link href="/store-locator">
              <MapPin className="h-4 w-4" />
              <span>Store Locator</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )}
    </>
  );
}

export function MainLayout({ children }: MainLayoutProps) {
  const [location] = useLocation();
  const { data: accessiblePages, isLoading } = usePageAccess();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Helper function to check if user can access a page
  const canAccess = (pageName: string) => {
    if (isLoading || !accessiblePages) return false;
    return accessiblePages.some(page => page.pageName === pageName);
  };
  
  return (
    <SidebarProvider defaultOpen={true}>
      <div className="flex h-screen w-full overflow-x-auto overflow-y-hidden">
        <Sidebar>
          <SidebarHeader className="flex items-center px-4 py-3 bg-byacre-light-blue">
            <div className="flex items-center gap-2">
              <img src={byAcreLogo} alt="byACRE Logo" className="h-6 w-6" />
              <span className="text-lg font-bold">Insights byACRE</span>
            </div>
            {/* Removed sidebar trigger as it's no longer needed */}
          </SidebarHeader>
          <SidebarContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/"}>
                  <Link href="/">
                    <Home className="h-4 w-4" />
                    <span>Dashboard Home</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/marketing"}>
                  <Link href="/marketing">
                    <LineChart className="h-4 w-4" />
                    <span>Performance Marketing</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/product-insights"}>
                  <Link href="/product-insights">
                    <BarChart2 className="h-4 w-4" />
                    <span>Product Insights</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/inventory-insights"}>
                  <Link href="/inventory-insights">
                    <Warehouse className="h-4 w-4" />
                    <span>Inventory Insights</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem className="pt-4">
                <div className="mb-2 px-3 text-xs font-semibold text-byacre-royal-blue">
                  E-commerce High Level
                </div>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/pricing" || location === "/pricing/trends"}>
                  <Link href="/pricing">
                    <DollarSign className="h-4 w-4" />
                    <span>Reseller Pricing</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/warehouse-stockout-analysis"}>
                  <Link href="/warehouse-stockout-analysis">
                    <BarChart2 className="h-4 w-4" />
                    <span>Warehouse Stockout Analysis</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/brand-tracking"}>
                  <Link href="/brand-tracking">
                    <TrendingUp className="h-4 w-4" />
                    <span>Brand Tracking Board</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem className="pt-4">
                <div className="mb-2 px-3 text-xs font-semibold text-byacre-royal-blue">
                  Paid Social
                </div>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/paidsocial/performance"}>
                  <Link href="/paidsocial/performance">
                    <Megaphone className="h-4 w-4" />
                    <span>Ad Performance</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/paidsocial/budget"}>
                  <Link href="/paidsocial/budget">
                    <Wallet className="h-4 w-4" />
                    <span>Budget Governor</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem className="pt-4">
                <div className="mb-2 px-3 text-xs font-semibold text-byacre-royal-blue">
                  Magento Integration
                </div>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/magento"}>
                  <Link href="/magento">
                    <ShoppingCart className="h-4 w-4" />
                    <span>Magento Stores</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/magento/products"}>
                  <Link href="/magento/products">
                    <Package className="h-4 w-4" />
                    <span>Magento Products</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/magento/orders"}>
                  <Link href="/magento/orders">
                    <FileText className="h-4 w-4" />
                    <span>Magento Orders</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem className="pt-4">
                <div className="mb-2 px-3 text-xs font-semibold text-byacre-royal-blue">
                  Amazon Integration
                </div>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/amazon/brand-analytics"}>
                  <Link href="/amazon/brand-analytics">
                    <BarChart2 className="h-4 w-4" />
                    <span>Brand Analytics</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/amazon/warehouse"}>
                  <Link href="/amazon/warehouse">
                    <Warehouse className="h-4 w-4" />
                    <span>Amazon FBA Warehouses</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/amazon/mcf"}>
                  <Link href="/amazon/mcf">
                    <Package className="h-4 w-4" />
                    <span>Amazon MCF Orders</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/amazon/orders"}>
                  <Link href="/amazon/orders">
                    <ShoppingCart className="h-4 w-4" />
                    <span>Amazon Orders</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem className="pt-4">
                <div className="mb-2 px-3 text-xs font-semibold text-byacre-royal-blue">
                  System Management
                </div>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/product-master-data"}>
                  <Link href="/product-master-data">
                    <Tags className="h-4 w-4" />
                    <span>Product Master Data</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-active={location === "/data-explorer"}>
                  <Link href="/data-explorer">
                    <Database className="h-4 w-4" />
                    <span>Data Explorer</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {canAccess("/meta") && (
                <>
                  <SidebarMenuItem className="pt-4">
                    <div className="mb-2 px-3 text-xs font-semibold text-byacre-royal-blue">
                      Meta Integration{isAdmin && ' *'}
                    </div>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild data-active={location === "/meta"}>
                      <Link href="/meta">
                        <Facebook className="h-4 w-4" />
                        <span>Meta API{isAdmin && ' *'}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}
              <CsSidebarSection location={location} />
              <DealerSidebarSection location={location} canAccessRole={canAccess("dealer-applications")} />
              <PmSidebarSection location={location} />
            </SidebarMenu>
          </SidebarContent>
          <SidebarFooter className="flex items-center px-4 py-2 bg-byacre-light-blue">
            <div className="text-xs text-byacre-royal-blue font-medium">
              © 2025 byACRE
            </div>
          </SidebarFooter>
        </Sidebar>
        <SidebarInset>
          <main className="flex-1 overflow-auto p-2 sm:p-3 lg:p-4 bg-gradient-to-br from-white to-[#e0eef5]/30">
            {children}
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}